import styled from 'styled-components'
import { useSearchParams } from 'react-router'
import clsx from 'clsx'
import {
    useTheme,
    CrossIcon,
    Button,
    Spinner,
    CartIcon
} from '@itsrever/design-system'
import { ShowMoreButton } from '@/components/FullCatalog'
import { useTranslation } from 'react-i18next'
import { useStoreProducts } from '@/hooks'
import { LoadingModal } from '@/components/Loading'
import { CartView } from '../Components/CartView'
import { useAppSelector } from '@/redux/hooks'
import { CartCreditBar } from '../Components/CartCreditBar'
import EmptyCart from '@/assets/images/EmptyCart'
import { usePostHog } from '@/application/use-posthog'
import { useCart } from '@/application/use-cart/use-cart'
import { StoreMenu } from '@/app/full-catalog/store-menu'
import { DEFAULT_COUNTRY_CODE } from '@/app/full-catalog/helpers/constants'
import { useStoreMenu } from '@/hooks/use-store-menu'
import { CreditFooter } from '@/app/full-catalog/credit-footer'
import { useDebounce } from '@/hooks/use-debounce'
import StoreProductCard from '@/app/full-catalog/products/store-product-card'
import { StoreSearch } from '@/app/full-catalog/store-search'

interface DesktopFullCatalogProps {
    returnedItemId: string
    handleClose: () => void
    handleContinue: () => void
    showCartView: boolean
    setShowCartView: (show: boolean) => void
}

export const DesktopFullCatalog: React.FC<DesktopFullCatalogProps> = ({
    returnedItemId,
    handleClose,
    handleContinue,
    showCartView,
    setShowCartView
}) => {
    const theme = useTheme()
    const { t, i18n } = useTranslation()

    const { captureEventPosthog } = usePostHog()

    const [searchParams] = useSearchParams()

    const { cartItems, getCartTotal } = useCart()

    const cartQuantity = getCartTotal(cartItems)

    const settings = useAppSelector(
        (store) => store.settingsApi.settings.response
    )

    const ecommerceId = useAppSelector(
        (store) => store.settingsApi.settings.response.ecommerce_id
    )

    const countryCode =
        useAppSelector((store) => store.return.returnAddress.country_code) ??
        DEFAULT_COUNTRY_CODE

    const { data, status } = useStoreMenu({
        ecommerceId,
        language: i18n.language,
        countryCode
    })

    const selectedCategory = searchParams.get('category')

    const selectedCategoryIndex = selectedCategory
        ? Number(selectedCategory)
        : 0

    const menu = data?.items ?? []

    const search = searchParams.get('filter_text') ?? ''

    const debouncedSearch = useDebounce(search, 300)

    const { loadNextPage, isLoadingProducts, products, pageInfo } =
        useStoreProducts(
            menu,
            selectedCategoryIndex,
            debouncedSearch ?? '',
            status === 'success'
        )

    const handleChangeCategory = () => {
        setShowCartView(false)
    }

    const onClickShowMore = () => {
        captureEventPosthog('FC - Click show more', {
            ecommerceId: settings.ecommerce_id
        })
        loadNextPage()
    }

    const onClickClose = () => {
        if (showCartView) {
            captureEventPosthog('FC - Click close Cart', {
                ecommerceId: settings.ecommerce_id
            })
            setShowCartView(false)
        } else {
            handleClose()
        }
    }

    const logoUrl = settings.branding?.logo_url
    const title = settings.branding?.title

    const isMisako = settings.ecommerce_group === 'misako'
    const isHoff = settings.ecommerce_group === 'hoff'

    if (isHoff) {
        return (
            <div className="absolute inset-x-0 top-0 z-[9] flex size-full bg-white">
                {status === 'pending' ? (
                    <LoadingModal loading />
                ) : (
                    <>
                        {status === 'error' ? (
                            <div className="flex size-full flex-col items-center justify-center gap-4">
                                <div className="opacity-75">
                                    <EmptyCart
                                        lightColor="#cbd5e1"
                                        darkColor="#f1f5f9"
                                    />
                                </div>
                                <div className="mb-20">
                                    <h3 className="mt-2 text-center text-2xl font-semibold">
                                        {t('full_catalog.menu_error_title')}
                                    </h3>
                                    <p className="mt-1 max-w-80 text-center text-base font-light text-slate-500">
                                        {t(
                                            'full_catalog.menu_error_description'
                                        )}
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <div className="relative mx-auto flex w-full flex-col">
                                <div className="mb-4 flex w-full items-center justify-between">
                                    <StoreMenu
                                        categories={data?.items}
                                        onChangeCategory={handleChangeCategory}
                                    />
                                    <div className="flex items-center gap-2">
                                        {!showCartView ? <StoreSearch /> : null}
                                        {!showCartView && (
                                            <div className="flex gap-2">
                                                <div className="">
                                                    <CartCreditBar
                                                        setShowCartView={
                                                            setShowCartView
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {showCartView ? (
                                    <div className="mb-8 mt-6">
                                        {cartQuantity > 0 && (
                                            <div className="mb-5 flex items-center justify-between gap-4">
                                                <h4 className="shrink-0 font-haas font-medium text-gray-800">
                                                    {t(
                                                        'full_catalog.your_selected_items'
                                                    )}
                                                </h4>
                                                <button
                                                    onClick={() => {
                                                        captureEventPosthog(
                                                            'FC - Click keep-exploring from cart'
                                                        )
                                                        setShowCartView(false)
                                                    }}
                                                    className="group flex items-center gap-1 p-2 font-haas text-black"
                                                >
                                                    <span>
                                                        <CartIcon scale={0.8} />
                                                    </span>
                                                    <p className="font-haas">
                                                        {t(
                                                            'full_catalog.keep_exploring'
                                                        )}
                                                    </p>
                                                </button>
                                            </div>
                                        )}

                                        <CartView
                                            handleContinue={handleContinue}
                                            returningItemId={returnedItemId}
                                            onClose={() =>
                                                setShowCartView(false)
                                            }
                                        />
                                    </div>
                                ) : (
                                    <>
                                        <h4 className="mb-4 shrink-0 font-haas font-medium text-gray-800">
                                            {search
                                                ? search
                                                : menu[selectedCategoryIndex]
                                                      ?.title}
                                        </h4>
                                        {isLoadingProducts ? (
                                            <div className="flex h-[100px] w-full items-center justify-center">
                                                <Spinner />
                                            </div>
                                        ) : (
                                            <ProductsContainer
                                                $show={
                                                    isLoadingProducts === false
                                                }
                                            >
                                                {products?.length > 0 ? (
                                                    <div className="w-full overflow-y-scroll">
                                                        <div
                                                            className="mb-10 grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(300px,1fr))]"
                                                            id="products-grid"
                                                        >
                                                            {products?.map(
                                                                (product) => {
                                                                    return (
                                                                        <StoreProductCard
                                                                            key={
                                                                                product.id
                                                                            }
                                                                            product={
                                                                                product
                                                                            }
                                                                            returnedItemId={
                                                                                returnedItemId
                                                                            }
                                                                            categoryIndex={
                                                                                selectedCategoryIndex
                                                                            }
                                                                        />
                                                                    )
                                                                }
                                                            )}
                                                        </div>
                                                        <div className="mb-36">
                                                            {pageInfo?.has_next_page &&
                                                                isLoadingProducts ===
                                                                    false && (
                                                                    <div className="flex">
                                                                        <ShowMoreButton
                                                                            onClick={() =>
                                                                                onClickShowMore()
                                                                            }
                                                                        />
                                                                    </div>
                                                                )}
                                                            {search !== '' &&
                                                                products?.length ===
                                                                    0 && (
                                                                    <div className="flex flex-col">
                                                                        <p className="mx-auto mt-4 text-xl text-gray-400">
                                                                            {t(
                                                                                'full_catalog.no_products_found'
                                                                            )}
                                                                        </p>
                                                                    </div>
                                                                )}
                                                            {!pageInfo?.has_next_page &&
                                                                products &&
                                                                products?.length >
                                                                    0 && (
                                                                    <div className="mb-7 mt-8 flex">
                                                                        <p className="mx-auto border-b-2 border-solid border-gray-400 pb-5 text-sm text-gray-400">
                                                                            {t(
                                                                                'full_catalog.showing_of',
                                                                                {
                                                                                    number: products?.length
                                                                                }
                                                                            )}
                                                                        </p>
                                                                    </div>
                                                                )}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="flex w-full flex-col items-center justify-center">
                                                        <p className="mt-6 text-center font-haas text-lg">
                                                            {t(
                                                                'full_catalog.empty_category_title'
                                                            )}
                                                        </p>
                                                        <p className="mt-1 max-w-80 text-center font-haas text-base font-light text-neutral-500">
                                                            {t(
                                                                'full_catalog.empty_category_description'
                                                            )}
                                                        </p>
                                                    </div>
                                                )}
                                            </ProductsContainer>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </>
                )}
                <div className="absolute bottom-5 right-5 flex justify-center">
                    {cartItems.length ? (
                        <button
                            onClick={handleContinue}
                            id="fc-continue-button"
                            className="w-fit min-w-44 cursor-pointer rounded-xl bg-black p-3 font-haas text-sm text-white disabled:cursor-not-allowed disabled:bg-neutral-500"
                        >
                            <span className="mx-6 flex items-center justify-center gap-3 font-haas text-base">
                                {t('full_catalog.confirm')}
                                <span className="flex size-6 items-center justify-center  text-center font-haas text-xs font-bold">
                                    {cartQuantity}
                                </span>
                            </span>
                        </button>
                    ) : null}
                </div>
            </div>
        )
    }

    if (isMisako) {
        return (
            <Container bgColor="#ffffff" className="!flex !flex-col">
                <div className="relative">
                    <div className="sticky top-0 z-10 flex w-full items-end bg-white lg:p-6">
                        {logoUrl ? (
                            <div className="mr-auto">
                                <img
                                    style={
                                        {
                                            '--logo-desktop-height': '1.5rem',
                                            '--logo-mobile-height': '1.2rem'
                                        } as React.CSSProperties
                                    }
                                    width={285}
                                    height={70}
                                    src={logoUrl}
                                    alt={title}
                                    className="h-[var(--logo-mobile-height)] w-auto object-contain lg:h-[var(--logo-desktop-height)]"
                                />
                            </div>
                        ) : null}
                        <div className="flex items-center gap-5 pr-2">
                            <a
                                href="https://www.misako.com/pages/wishlist"
                                target="_blank"
                                rel="noreferrer"
                            >
                                <img
                                    src="https://www.misako.com/cdn/shop/files/favorite.svg?v=1731587455"
                                    width={20}
                                    height={20}
                                />
                            </a>
                            <a
                                href="https://www.misako.com/account"
                                target="_blank"
                                rel="noreferrer"
                            >
                                <svg
                                    width="20"
                                    height="21"
                                    viewBox="0 0 20 21"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M19 20V17.7778C19 15.3222 16.9863 13.3333 14.5 13.3333H5.5C3.01375 13.3333 1 15.3222 1 17.7778V20"
                                        stroke="rgb(21, 21, 21)"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    ></path>
                                    <path
                                        d="M10 9C12.2091 9 14 7.20914 14 5C14 2.79086 12.2091 1 10 1C7.79086 1 6 2.79086 6 5C6 7.20914 7.79086 9 10 9Z"
                                        stroke="rgb(21, 21, 21)"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    ></path>
                                </svg>
                            </a>
                            <a
                                href="https://www.misako.com/collections/top-ventas"
                                target="_blank"
                                rel="noreferrer"
                            >
                                <svg
                                    width="19"
                                    height="19"
                                    viewBox="0 0 19 19"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M13.6969 13.9469L18 18.25M15.875 8.6875C15.875 12.7951 12.5451 16.125 8.4375 16.125C4.32988 16.125 1 12.7951 1 8.6875C1 4.57988 4.32988 1.25 8.4375 1.25C12.5451 1.25 15.875 4.57988 15.875 8.6875Z"
                                        stroke="rgb(21, 21, 21)"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    ></path>
                                </svg>
                            </a>
                        </div>
                    </div>
                    {status === 'pending' ? (
                        <LoadingModal loading />
                    ) : (
                        <>
                            {status === 'error' ? (
                                <div className="flex size-full flex-col items-center justify-center gap-4">
                                    <div className="opacity-75">
                                        <EmptyCart
                                            lightColor="#cbd5e1"
                                            darkColor="#f1f5f9"
                                        />
                                    </div>
                                    <div className="mb-20">
                                        <h3 className="mt-2 text-center text-2xl font-semibold">
                                            {t('full_catalog.menu_error_title')}
                                        </h3>
                                        <p className="mt-1 max-w-80 text-center text-base font-light text-slate-500">
                                            {t(
                                                'full_catalog.menu_error_description'
                                            )}
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className="relative flex w-full flex-1 flex-col px-5 py-6 lg:mx-auto lg:max-w-[1500px]">
                                    <div className="flex w-full items-start gap-4">
                                        <button
                                            className="-ml-2 -mt-0.5 flex cursor-pointer items-center justify-center pr-4 uppercase "
                                            onClick={onClickClose}
                                        >
                                            <svg
                                                width="24px"
                                                height="24px"
                                                strokeWidth="1"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                                color="#000000"
                                            >
                                                <path
                                                    d="M15 6L9 12L15 18"
                                                    stroke="#000000"
                                                    strokeWidth="1"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                ></path>
                                            </svg>
                                            Volver
                                        </button>
                                        <StoreMenu
                                            categories={data?.items}
                                            onChangeCategory={
                                                handleChangeCategory
                                            }
                                        />
                                        <CartCreditBar
                                            setShowCartView={setShowCartView}
                                        />
                                    </div>
                                    <div>
                                        <StoreSearch />
                                    </div>
                                    {showCartView ? (
                                        <div className="mb-8 mt-10">
                                            {cartQuantity > 0 && (
                                                <div className="mb-5 flex items-center justify-between gap-4">
                                                    <h4 className="shrink-0 text-xl font-[300] tracking-wider text-gray-800 [font-family:Inter]">
                                                        {t(
                                                            'full_catalog.your_selected_items'
                                                        )}
                                                    </h4>
                                                    <button
                                                        onClick={() => {
                                                            captureEventPosthog(
                                                                'FC - Click keep-exploring from cart'
                                                            )
                                                            setShowCartView(
                                                                false
                                                            )
                                                        }}
                                                        className="group flex items-center gap-1 border border-solid border-brand-500 p-2 text-brand-600 transition-all hover:bg-neutral-100"
                                                    >
                                                        <p className="text-[13px] uppercase tracking-wider [font-family:Inter]">
                                                            {t(
                                                                'full_catalog.keep_exploring'
                                                            )}
                                                        </p>
                                                    </button>
                                                </div>
                                            )}

                                            <CartView
                                                returningItemId={returnedItemId}
                                                onClose={() =>
                                                    setShowCartView(false)
                                                }
                                            />
                                        </div>
                                    ) : (
                                        <>
                                            {isLoadingProducts ? (
                                                <div className="mt-4 flex h-[100px] w-full items-center justify-center">
                                                    <Spinner color="#000000" />
                                                </div>
                                            ) : (
                                                <div
                                                    className={clsx(
                                                        'mt-4 overflow-y-auto',
                                                        'transition-all duration-500 ease-in',
                                                        {
                                                            flex:
                                                                isLoadingProducts ===
                                                                false,
                                                            hidden:
                                                                isLoadingProducts !==
                                                                false
                                                        }
                                                    )}
                                                >
                                                    {products?.length > 0 ? (
                                                        <div className="w-full overflow-y-auto">
                                                            <div
                                                                className="mb-10 grid gap-x-1.5 gap-y-16"
                                                                id="products-grid"
                                                                style={{
                                                                    gridTemplateColumns:
                                                                        'repeat(auto-fill, minmax(210px, 284px))'
                                                                }}
                                                            >
                                                                {products?.map(
                                                                    (
                                                                        product
                                                                    ) => {
                                                                        return (
                                                                            <StoreProductCard
                                                                                key={
                                                                                    product.id
                                                                                }
                                                                                product={
                                                                                    product
                                                                                }
                                                                                returnedItemId={
                                                                                    returnedItemId
                                                                                }
                                                                                categoryIndex={
                                                                                    selectedCategoryIndex
                                                                                }
                                                                            />
                                                                        )
                                                                    }
                                                                )}
                                                            </div>
                                                            <div className="mb-8">
                                                                {pageInfo?.has_next_page &&
                                                                    isLoadingProducts ===
                                                                        false && (
                                                                        <div className="flex pt-10">
                                                                            <ShowMoreButton
                                                                                onClick={() =>
                                                                                    onClickShowMore()
                                                                                }
                                                                            />
                                                                        </div>
                                                                    )}
                                                                {search !==
                                                                    '' &&
                                                                    products?.length ===
                                                                        0 && (
                                                                        <div className="flex flex-col">
                                                                            <p className="mx-auto mt-4 text-xl text-gray-400">
                                                                                {t(
                                                                                    'full_catalog.no_products_found'
                                                                                )}
                                                                            </p>
                                                                        </div>
                                                                    )}
                                                                {!pageInfo?.has_next_page &&
                                                                    products &&
                                                                    products?.length >
                                                                        0 && (
                                                                        <div className="mb-7 mt-8 flex">
                                                                            <p className="mx-auto border-b-2 border-solid border-gray-400 pb-5 text-sm text-gray-400">
                                                                                {t(
                                                                                    'full_catalog.showing_of',
                                                                                    {
                                                                                        number: products?.length
                                                                                    }
                                                                                )}
                                                                            </p>
                                                                        </div>
                                                                    )}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="flex size-full flex-1 flex-col items-center justify-center">
                                                            <p className="mb-2 mt-11 font-[300] tracking-wider text-[15xs] [font-family:Inter]">
                                                                {t(
                                                                    'full_catalog.empty_category_title'
                                                                )}
                                                            </p>
                                                            <p className="max-w-72 text-center text-[13px] font-[300] tracking-wider [font-family:Inter]">
                                                                {t(
                                                                    'full_catalog.empty_category_description'
                                                                )}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    )}
                                    {cartItems.length ? (
                                        <div className="sticky inset-x-0 bottom-6 mt-2 flex justify-center">
                                            <div className="flex flex-nowrap items-center gap-8">
                                                <Button
                                                    onClick={handleContinue}
                                                    id="fc-continue-button"
                                                >
                                                    <span className="mx-6 flex items-center justify-center gap-3 text-[13px] font-[300] uppercase tracking-wider [font-family:Inter]">
                                                        {t(
                                                            'full_catalog.confirm'
                                                        )}{' '}
                                                        ({cartQuantity})
                                                    </span>
                                                </Button>
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </Container>
        )
    }

    return (
        <Container bgColor={theme.colors.grey[5]}>
            {status === 'pending' ? (
                <LoadingModal loading />
            ) : (
                <>
                    <div>
                        <StoreMenu
                            categories={data?.items}
                            onChangeCategory={handleChangeCategory}
                        />
                    </div>
                    <div
                        className="absolute right-2 top-2 z-50 flex size-8 cursor-pointer items-center justify-center rounded-md p-2 hover:bg-gray-200"
                        onClick={onClickClose}
                    >
                        <CrossIcon />
                    </div>

                    {status === 'error' ? (
                        <div className="flex size-full flex-col items-center justify-center gap-4">
                            <div className="opacity-75">
                                <EmptyCart
                                    lightColor="#cbd5e1"
                                    darkColor="#f1f5f9"
                                />
                            </div>
                            <div className="mb-20">
                                <h3 className="mt-2 text-center text-2xl font-semibold">
                                    {t('full_catalog.menu_error_title')}
                                </h3>
                                <p className="mt-1 max-w-80 text-center text-base font-light text-slate-500">
                                    {t('full_catalog.menu_error_description')}
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="relative flex w-full flex-col px-6 pt-6 lg:mx-auto lg:max-w-[1500px]">
                            <div className="flex items-center justify-between gap-4">
                                <StoreSearch />
                                {!showCartView && (
                                    <div className="mr-5">
                                        <CartCreditBar
                                            setShowCartView={setShowCartView}
                                        />
                                    </div>
                                )}
                            </div>

                            {showCartView ? (
                                <div className="mb-8 mt-6">
                                    {cartQuantity > 0 && (
                                        <div className="mb-5 flex items-center justify-between gap-4">
                                            <h4 className="shrink-0 font-medium text-gray-800">
                                                {t(
                                                    'full_catalog.your_selected_items'
                                                )}
                                            </h4>
                                            <button
                                                onClick={() => {
                                                    captureEventPosthog(
                                                        'FC - Click keep-exploring from cart',
                                                        {
                                                            ecommerceId:
                                                                settings.ecommerce_id
                                                        }
                                                    )
                                                    setShowCartView(false)
                                                }}
                                                className="group flex items-center gap-1 rounded-lg border border-solid border-brand-500 p-2 text-brand-600 transition-all hover:scale-105 hover:bg-brand-100"
                                            >
                                                <span className="transition-all duration-300 group-hover:rotate-3 group-hover:scale-125">
                                                    <CartIcon scale={0.8} />
                                                </span>
                                                <p className="font-semibold">
                                                    {t(
                                                        'full_catalog.keep_exploring'
                                                    )}
                                                </p>
                                            </button>
                                        </div>
                                    )}

                                    <CartView
                                        returningItemId={returnedItemId}
                                        onClose={() => setShowCartView(false)}
                                    />
                                </div>
                            ) : (
                                <>
                                    <h4 className="mb-4 mt-6 shrink-0 font-medium text-gray-800">
                                        {search
                                            ? search
                                            : menu[selectedCategoryIndex]
                                                  ?.title}
                                    </h4>
                                    {isLoadingProducts ? (
                                        <div className="flex h-[100px] w-full items-center justify-center">
                                            <Spinner />
                                        </div>
                                    ) : (
                                        <ProductsContainer
                                            $show={isLoadingProducts === false}
                                        >
                                            {products?.length > 0 ? (
                                                <div className="w-full overflow-y-scroll">
                                                    <ItemsDiv
                                                        className="mb-10"
                                                        id="products-grid"
                                                    >
                                                        {products?.map(
                                                            (product) => {
                                                                return (
                                                                    <StoreProductCard
                                                                        key={
                                                                            product.id
                                                                        }
                                                                        product={
                                                                            product
                                                                        }
                                                                        returnedItemId={
                                                                            returnedItemId
                                                                        }
                                                                        categoryIndex={
                                                                            selectedCategoryIndex
                                                                        }
                                                                    />
                                                                )
                                                            }
                                                        )}
                                                    </ItemsDiv>
                                                    <div className="mb-36">
                                                        {pageInfo?.has_next_page &&
                                                            isLoadingProducts ===
                                                                false && (
                                                                <div className="flex">
                                                                    <ShowMoreButton
                                                                        onClick={() =>
                                                                            onClickShowMore()
                                                                        }
                                                                    />
                                                                </div>
                                                            )}
                                                        {search !== '' &&
                                                            products?.length ===
                                                                0 && (
                                                                <div className="flex flex-col">
                                                                    <p className="mx-auto mt-4 text-xl text-gray-400">
                                                                        {t(
                                                                            'full_catalog.no_products_found'
                                                                        )}
                                                                    </p>
                                                                </div>
                                                            )}
                                                        {!pageInfo?.has_next_page &&
                                                            products &&
                                                            products?.length >
                                                                0 && (
                                                                <div className="mb-7 mt-8 flex">
                                                                    <p className="mx-auto border-b-2 border-solid border-gray-400 pb-5 text-sm text-gray-400">
                                                                        {t(
                                                                            'full_catalog.showing_of',
                                                                            {
                                                                                number: products?.length
                                                                            }
                                                                        )}
                                                                    </p>
                                                                </div>
                                                            )}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="flex w-full flex-col items-center justify-center">
                                                    <div className="opacity-75">
                                                        <EmptyCart
                                                            lightColor="#cbd5e1"
                                                            darkColor="#f1f5f9"
                                                        />
                                                    </div>
                                                    <h3 className="mt-6 text-center text-2xl font-semibold">
                                                        {t(
                                                            'full_catalog.empty_category_title'
                                                        )}
                                                    </h3>
                                                    <p className="mt-1 max-w-80 text-center text-base font-light text-slate-500">
                                                        {t(
                                                            'full_catalog.empty_category_description'
                                                        )}
                                                    </p>
                                                </div>
                                            )}
                                        </ProductsContainer>
                                    )}
                                </>
                            )}
                            <div className="absolute inset-x-0 bottom-0 mx-auto w-fit bg-transparent">
                                <CreditFooter onContinue={handleContinue} />
                            </div>
                        </div>
                    )}
                </>
            )}
        </Container>
    )
}

const Container = styled.div<{ bgColor: string }>`
    z-index: 9;
    display: flex;
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background-color: ${(p) => p.bgColor};
`

const ItemsDiv = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
    gap: 1rem;
`
interface ProductsContainerProps {
    $show: boolean
}
const ProductsContainer = styled.div<ProductsContainerProps>`
    overflow-y: scroll;
    display: ${(p) => (p.$show ? 'flex' : 'none')};
    transition: all 0.5s ease-in;
`
